import { useEffect, useState } from "react";

import { Head } from "@unhead/react";
import { useLocation, useNavigate } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box } from "@/components/layout/flexbox";
import { PublicFooter } from "@/components/nav/PublicFooter";
import { PublicNav } from "@/components/nav/PublicNav";
import { Benefits } from "@/components/pieces/marketing/Benefits";
import { Brands } from "@/components/pieces/marketing/Brands";
import { CoachShowcase } from "@/components/pieces/marketing/CoachShowcase";
import { CreditSystem } from "@/components/pieces/marketing/CreditSystem";
import { GoldBar } from "@/components/pieces/marketing/GoldBar";
import { Testimonials } from "@/components/pieces/marketing/Testimonials";
import { Typeform } from "@/components/pieces/marketing/Typeform";
import { UseCases } from "@/components/pieces/marketing/UseCases";
import { UserExperience } from "@/components/pieces/marketing/UserExperience";

import { MainHero } from "./MainHero";
import bizLeadershipCoaching from "./VariantBizLeadershipCoaching";
import careerCoaching from "./VariantCareerCoaching";
import execLeadershipCoaching from "./VariantExecLeadershipCoaching";
import jobSearchCoaching from "./VariantJobSearchCoaching";
import leadershipCoaching from "./VariantLeadershipCoaching";
import lifeCoaching from "./VariantLifeCoaching";

const SuccessUrl = "#success";
export const TYPEFORM_ID = IS_PROD ? "zaGBf013" : "yfrRIkkU";

// Populate a map of url slug to variant configurations
const Variants = {
  [careerCoaching.options.page]: careerCoaching,
  [lifeCoaching.options.page]: lifeCoaching,
  [leadershipCoaching.options.page]: leadershipCoaching,
  [bizLeadershipCoaching.options.page]: bizLeadershipCoaching,
  [jobSearchCoaching.options.page]: jobSearchCoaching,
};

const ExecVariants = {
  [execLeadershipCoaching.options.page]: execLeadershipCoaching,
};

const DefaultVariant = IS_EXEC ? execLeadershipCoaching : careerCoaching;

export function PlacementLPCoachingPage({ basePage }: { basePage: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showingForm, setShowingForm] = useState(
    location.hash.includes("form"),
  );
  const [showingSuccess, setShowingSuccess] = useState(false);

  let path = "";
  if (!basePage) {
    path = location.pathname.slice(1);
    basePage = path.toUpperCase().replace(/-/g, "_");
  }

  const { options, benefits } = IS_EXEC
    ? ExecVariants[basePage] || DefaultVariant
    : Variants[basePage] || DefaultVariant;

  useEffect(() => {
    if (!showingForm && location.hash.includes("form")) {
      setShowingForm(true);
    }
  }, [location.hash]);

  return (
    <>
      <SEOHead
        title={options.metaTitle}
        description={options.metaDescription}
        image={options.metaImage}
        canonicalPath={`/${basePage.toLowerCase().replace(/_/g, "-")}`}
      />
      <Head>
        <style type="text/css">{`
          #root {
            overflow-x: hidden;
          }
        `}</style>
      </Head>
      <PublicNav
        ctaLink={
          IS_EXEC
            ? undefined
            : options.ctaLink || `/match/${options.specialtySlug}`
        }
      />

      <MainHero
        options={options}
        onCtaClick={
          options.usePopupForm ? () => setShowingForm(true) : undefined
        }
      />
      <Brands opacity={1} backgroundColor={options.brandBg} />
      <GoldBar />
      <Benefits
        title={benefits.title}
        subtitle={benefits.subtitle}
        ctaText={
          "ctaButtonText" in benefits
            ? benefits.ctaButtonText
            : "Book a Session"
        }
        ctaLink={"ctaLink" in benefits ? benefits.ctaLink : undefined}
        benefits={benefits.items}
        onCtaClick={
          options.usePopupForm ? () => setShowingForm(true) : undefined
        }
      />
      <UserExperience
        audience={options.audience}
        ctaText={
          options.hideUserExperienceButton
            ? undefined
            : options.ctaButtonText || "Book a Session"
        }
        ctaLink={options.ctaLink || `/match/${options.specialtySlug}`}
      />
      <GoldBar />
      <UseCases audience={options.audience} useCases={options.useCases} />
      <CoachShowcase
        key={basePage}
        specialtySlug={options.specialtySlug}
        header={options.coachShowcaseHeader}
      />
      <Testimonials
        buttonLink={options.ctaLink}
        buttonText={options.ctaButtonText || "Book a Session"}
        pricingIntro={options.pricingIntro}
        specialtySlug={options.specialtySlug}
        header={options.testimonialsHeader}
        onCtaClick={
          options.usePopupForm ? () => setShowingForm(true) : undefined
        }
      />
      <CreditSystem
        header={options.creditSystemHeader}
        audience={options.audience}
        ctaText={options.ctaButtonText || "Book a Session"}
        ctaLink={options.ctaLink || `/match/${options.specialtySlug}`}
        onCtaClick={
          options.usePopupForm ? () => setShowingForm(true) : undefined
        }
      />
      <PublicFooter />
      {!import.meta.env.SSR && showingForm && (
        <Typeform
          id={TYPEFORM_ID}
          visible={showingForm}
          setVisible={setShowingForm}
          success={
            showingSuccess ? (
              <Box textAlign="center" p={6} fontSize={24}>
                Thank you! Our team will be in touch soon.
              </Box>
            ) : null
          }
          onSubmit={() => {
            setShowingSuccess(true);
            navigate(`/${path}${SuccessUrl}`, { replace: true });
          }}
        />
      )}
    </>
  );
}
